import { useQuery } from "@apollo/client";
import CheckIcon from "@mui/icons-material/Check";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { GetActivityTypesQuery } from "@/graphql/graphql";
import * as sx from "@/pages/ActivityLibrary/components/ActivityCreatePage/ActivityCreatePage.styles";
import { GET_ACTIVITY_TYPES_QUERY } from "@/pages/UserDetails/components/CallPlans/CreateCallPlanModal/ActivitiesTable";
import { compareActivityTypes } from "@/utils/activities";

export type ActivityTypeKeysPickerProps = {
  value: string[] | undefined;
  onChange: (keys: string[]) => void;
};

/**
 * Multi-select activity type keys from API data. Uses early returns instead of nested ternaries.
 */
export const ActivityTypeKeysPicker: React.FC<ActivityTypeKeysPickerProps> = ({
  value,
  onChange,
}) => {
  const {
    data: activityTypesData,
    loading,
    error,
  } = useQuery<GetActivityTypesQuery>(GET_ACTIVITY_TYPES_QUERY, {
    fetchPolicy: "cache-and-network",
  });
  const [sortedActivityTypes, setSortedActivityTypes] = useState<
    GetActivityTypesQuery["activityTypes"]
  >([]);
  const selectedKeys = Array.isArray(value) ? value : value ? [value] : [];

  useEffect(() => {
    if (activityTypesData) {
      setSortedActivityTypes([...activityTypesData.activityTypes].sort(compareActivityTypes));
    }
  }, [activityTypesData]);

  if (error) {
    return (
      <Alert severity="error" sx={sx.activityCreateAlertInlineSx}>
        Could not load activity types. Try again later.
      </Alert>
    );
  }

  if (loading && sortedActivityTypes.length === 0) {
    return (
      <Box sx={sx.activityCreateActivityTypesLoadingWrapSx}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (sortedActivityTypes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No activity types available.
      </Typography>
    );
  }

  return (
    <Box sx={sx.activityCreateActivityTypeGridSx}>
      {sortedActivityTypes.map((activityType) => {
        const active = selectedKeys.includes(activityType.activityTypeKey);
        return (
          <Button
            key={activityType.activityTypeKey}
            onClick={() => {
              if (active) {
                onChange(selectedKeys.filter((item) => item !== activityType.activityTypeKey));
                return;
              }
              onChange([...selectedKeys, activityType.activityTypeKey]);
            }}
            sx={sx.activityCreateActivityTypeButtonSx(active)}
          >
            <Box sx={sx.activityCreateCheckboxBoxSx(active)}>
              {active && <CheckIcon sx={sx.activityCreateActivityTypeCheckIconSx} />}
            </Box>
            <Typography variant="body2" sx={sx.activityCreateActivityTypeRowLabelSx}>
              {activityType.activityTypeLabel}
            </Typography>
          </Button>
        );
      })}
    </Box>
  );
};
